import CustomButton from '@/components/atoms/CustomButton';
import RadioButtonGroup from '@/components/molecules/RadioButtonGroup';
import AppPageTemplate from '@/components/templates/AppPageTemplate';
import { NOTIFICATION_CHANNELS, NOTIFICATION_CHANNELS_SETTINGS_TITLE_AND_DESCRIPTION } from '@/constants/stringVars';
import { useNotificationPreferences } from '@/hooks/user/useNotificationPreferences';
import useUpdateNotificationPreferences from '@/hooks/user/useUpdateNotificationPreferences';
import { TNotificationChannel } from '@/types/Notification.types';
import { Center, HStack, Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

type TNotificationSetting = 'On' | 'Off';

const Settings = () => {
  const {
    data: notificationPreferences,
    refetch: refetchNotificationPreferences,
    isLoading,
  } = useNotificationPreferences();

  useEffect(() => {
    if (notificationPreferences) setNewNotificationPreferences(notificationPreferences);
  }, [notificationPreferences]);

  const [newNotificationPreferences, setNewNotificationPreferences] = useState<TNotificationChannel[]>([]);

  const notificationSettingsChanged = useMemo(
    () => newNotificationPreferences.sort().toString() !== notificationPreferences?.sort().toString(),
    [notificationPreferences, newNotificationPreferences],
  );

  const { mutate: updateNotificationPreferences, isPending: isNotificationSettingsUpdatePending } =
    useUpdateNotificationPreferences(refetchNotificationPreferences);

  const onNotificationSettingChange = useCallback(
    (updatedChannel: TNotificationChannel, newValue: TNotificationSetting) => {
      const updatedNotificationPreferences: TNotificationChannel[] = [...(newNotificationPreferences ?? [])];
      if (newValue === 'On') {
        if (!updatedNotificationPreferences.includes(updatedChannel)) {
          updatedNotificationPreferences.push(updatedChannel);
        }
      } else {
        updatedNotificationPreferences.splice(
          updatedNotificationPreferences.findIndex((notificationChannel) => notificationChannel === updatedChannel),
          1,
        );
      }

      setNewNotificationPreferences(updatedNotificationPreferences);
    },
    [newNotificationPreferences],
  );

  const onConfirmNotificationSettingsUpdate = useCallback(
    () => updateNotificationPreferences(newNotificationPreferences),
    [newNotificationPreferences],
  );

  return (
    <AppPageTemplate>
      {/* Notifications */}
      <VStack gap={'20px'} marginBottom={'80px'}>
        <VStack gap={0}>
          <Text variant={'loraSmallTitle'}>Notifications</Text>
          <Text variant={'urbanistSemiBold'} marginY={'14px'} color={'text.mediumGray'}>
            Select which notifications you want to receive and how.
          </Text>
        </VStack>

        <VStack width={'620px'} gap={'40px'}>
          {NOTIFICATION_CHANNELS.map((notificationChannel) => (
            <NotificationSettingsDisplayRow
              key={notificationChannel}
              channel={notificationChannel}
              value={notificationPreferences?.includes(notificationChannel) ? 'On' : 'Off'}
              onChange={onNotificationSettingChange}
              disabled={isLoading}
            />
          ))}
        </VStack>

        <Center marginTop={'20px'}>
          <CustomButton
            backgroundColor="primary.500"
            labelColor="extra.white"
            label={'Confirm Update'}
            onClick={onConfirmNotificationSettingsUpdate}
            width={'210px'}
            height={'55px'}
            labelStyle={{ fontWeight: 700 }}
            disabled={!notificationSettingsChanged || !notificationPreferences}
            isLoading={isNotificationSettingsUpdatePending}
          />
        </Center>
      </VStack>
    </AppPageTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/settings/')({
  component: Settings,
});

const NotificationSettingsDisplayRow = ({
  channel,
  value,
  onChange,
  disabled,
}: {
  channel: TNotificationChannel;
  value: TNotificationSetting;
  onChange: (channel: TNotificationChannel, newValue: TNotificationSetting) => void;
  disabled: boolean;
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <HStack width={'100%'} justifyContent={'space-between'}>
      <VStack align={'start'}>
        <Text variant={'urbanistBoldLarge'} color={'text.darkBlue'}>
          {NOTIFICATION_CHANNELS_SETTINGS_TITLE_AND_DESCRIPTION[channel].title}
        </Text>
        <Text variant={'urbanistSemiBold'} color={'text.mediumGray'}>
          {NOTIFICATION_CHANNELS_SETTINGS_TITLE_AND_DESCRIPTION[channel].subtitle}
        </Text>
      </VStack>
      <HStack>
        <RadioButtonGroup
          width={'100px'}
          items={['On', 'Off']}
          disabled={disabled}
          selectedItem={localValue}
          onClick={(newValue: string) => {
            setLocalValue(newValue as TNotificationSetting);
            onChange(channel, newValue as TNotificationSetting);
          }}
        />
      </HStack>
    </HStack>
  );
};
